import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import * as moment from 'moment-timezone';
import { DialogsService } from '../../../../../core/services/dialog.service';
import { CaptainDAO } from '../../../../../core/services/dao/captainDAO';
import { Captain } from 'anchor-shared/lib/model/entity/captain';
import { User } from 'anchor-shared/lib/model/entity/user';
import { UserDAO } from '../../../../../core/services/dao/userDAO';
import { LoginPersistenceService } from '../../../../../core/services/loginPersistence.service';
import { AuthService } from '../../../../../core/services/auth.service';
import { BookingDAO } from '../../../../../core/services/dao/bookingDAO';
import { DiscountDAO } from '../../../../../core/services/dao/discountDAO';
import { ContainsSubscriptions } from '../../../../../core/services/interfaces/contains-subscriptions';
import { unsubscribeFromAll } from '../../../../../core/pure-functions/rx-helper';
import { Discount, PromoType } from 'anchor-shared/lib/model/entity/discount';

@Component({
  selector: 'app-captain-info',
  templateUrl: './captain-info.component.html',
  styleUrls: ['./captain-info.component.scss'],
})
export class CaptainInfoComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  subs: Subscription[] = [];

  model: any = {};

  captainId: number;
  captainName: string;
  captainRate: number;
  routeSubscription: Subscription;

  user: User;
  isLoggedIn = false;
  startDate: any;
  maxDate: any;
  minDuration: number;
  maxDuration: number;
  discount: Discount;
  discountAmount: number;
  timezone: string;
  timeAbbrev: any;
  requestBookingClicked = false;

  captain: Captain;
  captainUser: User;
  times: string[] = [
    '8:00 AM',
    '8:30 AM',
    '9:00 AM',
    '9:30 AM',
    '10:00 AM',
    '10:30 AM',
    '11:00 AM',
    '11:30 AM',
    '12:00 PM',
    '12:30 PM',
    '1:00 PM',
    '1:30 PM',
    '2:00 PM',
    '2:30 PM',
    '3:00 PM',
    '3:30 PM',
    '4:00 PM',
    '4:30 PM',
    '5:00 PM',
    '5:30 PM',
    '6:00 PM',
    '6:30 PM',
    '7:00 PM',
    '7:30 PM',
    '8:00 PM',
    '8:30 PM',
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userDAO: UserDAO,
    private captainDAO: CaptainDAO,
    private discountDAO: DiscountDAO,
    private authenticationService: AuthService,
    public dialog: DialogsService,
    public loginPersistenceService: LoginPersistenceService,
    public bookingDAO: BookingDAO,
  ) {
    const dataSub = this.route.data.subscribe((data) => {
      this.captain = data.portal.captainResolve;
    });
    this.subs.push(dataSub);
    this.captainId = route.snapshot.params.id;
    this.startDate = moment();
    this.maxDate = moment().add(6, 'months');
    this.model.duration = this.minDuration = 4;
    this.maxDuration = 12;
    this.isLoggedIn = this.authenticationService.isLoggedIn();
    this.timezone = moment.tz.guess();
    this.timeAbbrev = moment.tz.zone(this.timezone).abbr(moment.tz());
  }

  ngOnInit() {
    this.captainUser = this.captain['user'];
    this.captainRate = this.captain.hireRate;
    this.captainName =
      this.captainUser.firstName + ' ' + this.captainUser.lastName.charAt(0);
    if (this.loginPersistenceService.getHasBookingData()) {
      this.model = this.loginPersistenceService.getBookingModel();
      this.captain = this.loginPersistenceService.getCaptain();
    }
    this.validateCode();
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    unsubscribeFromAll(this.subs);
  }

  onSubmit() {
    if (!this.requestBookingClicked) {
      this.requestBookingClicked = true;
      if (!this.isLoggedIn) {
        this.loginPersistenceService.setBookingModel(this.model);
        this.loginPersistenceService.setHasBookingData(true);
        this.loginPersistenceService.setCaptain(this.captain);
        this.router.navigate(['sign-in-needed']);
      } else {
        this.bookingCreate();
      }
    }
  }

  bookingCreate() {
    this.user = this.userDAO.getCurrentUser();
    let bookingDate = moment(this.model.date).format('MM/DD/YYYY');
    if (this.discountAmount) {
      this.discount.amount = this.discountAmount;
    }
    const arrivalTimeStr = bookingDate + ' ' + this.model.arrivalTime;
    const arrivalTime = moment(arrivalTimeStr).tz('UTC');
    bookingDate = moment(arrivalTimeStr).tz('UTC').format('MM/DD/YYYY');
    const bookingData = {
      bookingDate: bookingDate,
      duration: this.model.duration,
      arrivalTime: arrivalTime,
      arrivalStreet: this.model.street,
      arrivalCity: this.model.city,
      arrivalState: this.model.state,
      arrivalZip: this.model.zip,
      arrivalNotes: this.model.notes,
      bookingTotal:
        this.discount && this.discount.amount
          ? this.captainRate * this.model.duration - this.discountAmount
          : this.captainRate * this.model.duration,
      passengerId: this.user.id,
      captainId: this.captain.id,
      waterBodyId: null,
      discountId: this.discount ? this.discount.id : null,
      localTimezone: this.timezone,
    };
    this.bookingDAO.addNewBooking(bookingData).subscribe((result) => {
      this.loginPersistenceService.setHasBookingData(false);
      this.router.navigate(['booking', result.id]);
    });
  }

  openTos() {
    this.dialog.TermsOfService().subscribe();
  }

  validateCode() {
    const discountSub = this.discountDAO.get().subscribe((discountData) => {
      let discounts: Discount[] = discountData;
      for (let discount of discounts) {
        if (
          this.model.code &&
          discount.code === this.model.code.toUpperCase()
        ) {
          if (
            discount.active &&
            discount.promoType === PromoType.CAPTAIN_FOR_HIRE
          ) {
            if (discount.percentageBased) {
              const bookingTotal = this.model.duration * this.captainRate;
              const discountPercentage = discount.amount / 100;
              const discountTotal = bookingTotal * discountPercentage;
              this.discountAmount = discountTotal;
              this.discount = discount;
              return;
            }
            this.discount = discount;
            this.discountAmount = discount.amount;
            return;
          }
          this.discount = null;
          return;
        }
      }
      this.discount = null;
    });
    this.subs.push(discountSub);
  }

  getCaptainName(captain: Captain): string {
    const lastInitial = captain.user.lastName.charAt(0);
    if (captain.uscgLicensed) {
      return (
        'Capt. ' + captain.user.firstName + ' ' + lastInitial.toUpperCase()
      );
    } else {
      return captain.user.firstName + ' ' + lastInitial.toUpperCase();
    }
  }
}
